"use client"
import { FooterContentDto } from "api/generated/MazdaInterfaces"
import NextImage from "common/bit/NextImage"
import NextLink from "common/bit/NextLink"
import Image from "next/image"
import styled from "styled-components"
import { HomePageContainer } from "theme/shared"
import FooterColumn from "./FooterColumn"
import FooterSocialRow from "./FooterSocialRow"

// The footer switches from the stacked mobile layout to the desktop grid at 820px (DLK-557).
const DESKTOP = "@media (min-width: 820px)"
const SMALL_LAPTOP = (p) => p.theme.breakpoints.down("smallLaptop")

const FooterRoot = styled.footer`
  position: relative;
  width: 100%;
  overflow: hidden;
  background-color: ${(p) => p.theme.colors.darkBlack};
  display: flex;
  flex-direction: column;
  gap: 27.61px;
  ${DESKTOP} {
    gap: 45px;
  }
`

/* ------------------------------- Mobile ------------------------------- */

const MobileView = styled.div`
  display: block;
  ${DESKTOP} {
    display: none;
  }
`

const MobileDancers = styled.div`
  position: relative;
  width: 100%;
  height: 250px;
`

const MobileContent = styled.div`
  display: flex;
  flex-direction: column;

  gap: 30px;
  padding: 30px 20px 0;
`

const MobileLogo = styled.div``

// Cells are half-width rather than content-width so a row always holds exactly two
// groups — sizing them to their content would let short labels squeeze in a third.
// Capped at two 148px columns (the Column cap below md) so the block keeps its
// shape on wide phones instead of spreading toward the fold (DLK-890).
const MobileGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 56px;
  width: 100%;
  max-width: 352px;

  > * {
    flex: 0 0 calc(50% - 28px);
  }
  ${SMALL_LAPTOP} {
    gap: 30px 56px;
  }
`

/* ------------------------------ Desktop ------------------------------- */

const DesktopView = styled.div`
  display: none;
  ${DESKTOP} {
    display: block;
  }
`

const DesktopMain = styled(HomePageContainer)`
  position: relative;
  margin: 0 auto;
  padding-top: 64px;
  padding-bottom: 0;
  min-height: 622px;
  display: flex;
  ${SMALL_LAPTOP} {
    padding-left: 20px;
    padding-right: 20px;
  }
`

const DesktopDancers = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: min(45vw, 832px);
  height: 521px;
  z-index: 0;
  pointer-events: none;
`

const DesktopInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1440px;
  margin: 0 auto;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: space-between;
`

const DesktopTopRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 120px;
`

// Capping the group block keeps it wrapping in place instead of stretching across
// the dancers image. How many groups land on a row follows their content width —
// three wide ones, more when the labels are short (DLK-890).
const DesktopColumns = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 56px;
  max-width: 760px;
  @media (max-width: 1400px) {
    max-width: 600px;
  }
`

const DesktopLogo = styled.div`
  flex-shrink: 0;
`

/* ------------------------------- Credit ------------------------------- */

const CreditBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  padding: 0 0 12px 0;
  width: 100%;
  a {
    height: 20px;
  }
  ${DESKTOP} {
    padding: 12px 0;
  }
`

const DELEK_LOGO = { src: "/img/delek-motors-logo.png", alt: "דלק מוטורס" }
const DANCERS_ALT = "רקדנים"

const Footer = ({ footerData }: { footerData: FooterContentDto }) => {
  const columns = footerData?.columns ?? []

  // Mobile keeps the contact column on its own full-width row — it carries the chat
  // CTA and the phone/mail rows, which do not survive a half-width cell. Every group
  // after it shares a 2-up grid, so any number of groups wraps instead of running off
  // the side (DLK-890, replacing the fixed first/middle/last split from DLK-557).
  // Groups follow CMS array order (models on the right, tools on the left). NOTE: the
  // Figma mobile frame shows the reverse (tools on the right) — flagged for QA/design.
  const firstColumn = columns[0]
  const restColumns = columns.slice(1)

  return (
    <FooterRoot>
      {/* -------- Mobile -------- */}
      <MobileView>
        <MobileDancers>
          <NextImage
            image={{
              imageUrl: "/img/footer-dancers-mobile.png",
              alt: DANCERS_ALT,
            }}
            cover
          />
        </MobileDancers>
        <MobileContent>
          <MobileLogo>
            <Image
              src={DELEK_LOGO.src}
              alt={DELEK_LOGO.alt}
              width={177}
              height={36}
            />
          </MobileLogo>
          {firstColumn && (
            <FooterColumn column={firstColumn} variant="mobile" />
          )}
          {!!restColumns.length && (
            <MobileGrid>
              {restColumns.map((column) => (
                <FooterColumn
                  key={column.id}
                  column={column}
                  variant="mobile"
                />
              ))}
            </MobileGrid>
          )}
          {footerData?.social && (
            <FooterSocialRow social={footerData.social} variant="mobile" />
          )}
        </MobileContent>
      </MobileView>

      {/* -------- Desktop -------- */}
      <DesktopView>
        <DesktopMain>
          <DesktopDancers>
            <NextImage
              image={{ imageUrl: "/img/footer-dancers.png", alt: DANCERS_ALT }}
              cover={false}
              objectPosition="bottom left"
            />
          </DesktopDancers>
          <DesktopInner>
            <DesktopTopRow>
              <DesktopColumns>
                {columns.map((column) => (
                  <FooterColumn
                    key={column.id}
                    column={column}
                    variant="desktop"
                  />
                ))}
              </DesktopColumns>
              <DesktopLogo>
                <Image
                  src={DELEK_LOGO.src}
                  alt={DELEK_LOGO.alt}
                  width={177}
                  height={36}
                />
              </DesktopLogo>
            </DesktopTopRow>
            {footerData?.social && (
              <FooterSocialRow social={footerData.social} variant="desktop" />
            )}
          </DesktopInner>
        </DesktopMain>
      </DesktopView>

      {/* -------- Created By Mint (hardcoded) -------- */}
      <CreditBar>
        <NextLink openInNewTab href="https://mintapp.co.il">
          <Image
            width={134.4}
            height={20}
            src="/img/createdByMintMobile.png"
            alt="נבנה על ידי mint"
            style={{
              maxWidth: "100%",
              height: "auto",
              aspectRatio: "134.4 / 20",
              objectFit: "contain",
            }}
          />
        </NextLink>
      </CreditBar>
    </FooterRoot>
  )
}

export default Footer
